import React, { useState, useContext } from "react";
import { Modal, Row, Col, Form, Button, Table } from "react-bootstrap";
import { AuthContext } from "../../contexts/AuthContext";
import { formatDateInDDMMYYYY, formatDateTimeInDDMMYYYY, getCurrentDateTime } from "../../functions/common-functions";
import { updateSalesOrder } from "../../services/maintain-bid-service";
import { addLogDetails } from "../../services/logger-entry-service";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import successImage from "../../icons/success-tick-logo.svg";
import errorImage from "../../icons/error-tick-logo.svg";

const UpdateSalesOrderModal = ({ show, onHide, enquiry, setReload, reload, allEnquiries }) => {
  const MySwal = withReactContent(Swal);
  const context = useContext(AuthContext);
  const [salesOrderData, setSalesOrderData] = useState({ SONumber: enquiry?.SONumber || '' });
  const [errors, setErrors] = useState({});

  const formatEnquirySource = (value) => {
    if (value === "Manual" || value === "Disposal") return "Non-Contract";
    if (value === "Contract") return "Contract";
    return value || "";
  };

  const handleDownload = async (filePath, fileName) => {
    if (!filePath) return;
    try {
      const response = await fetch(`/api/files/download?filePath=${encodeURIComponent(filePath)}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${context.token}` },
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName || filePath.split('/').pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const handleSalesOrderChange = (value) => {
    if (value.length > 550 || (value && !/^[a-zA-Z0-9,]*$/.test(value))) return;
    setSalesOrderData({ SONumber: value });
    if (errors.salesOrder) setErrors(prev => ({ ...prev, salesOrder: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!salesOrderData.SONumber.trim()) newErrors.salesOrder = "Sales Order Number is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    // Check SO number uniqueness from loaded data
    const isDuplicate = allEnquiries.some(
      (enq) => enq.SONumber === salesOrderData.SONumber && enq.EnquiryNumber === enquiry.EnquiryNumber && enq.LogisticCompany === enquiry.LogisticCompany
    );

    if (isDuplicate) {
      MySwal.fire({
        text: "Sales Order Number already exists. Please use a different number.",
        imageUrl: errorImage,
        customClass: "swal-error",
      });
      return;
    }

    try {
      const currentDateTime = getCurrentDateTime("-");
      const updateData = {
        EnquiryNumber: enquiry.EnquiryNumber,
        CustomerEmail: enquiry.CustomerEmail,
        CustomerCode: enquiry.CustomerCode || enquiry.BPCode || "",
        SONumber: salesOrderData.SONumber,
        CreatedBy: context.userId,
        CreatedByName: context.userName,
        CreatedOn: currentDateTime,
        ModifiedBy: context.userId,
        ModifiedByName: context.userName,
        ModifiedOn: currentDateTime
      };

      const result = await updateSalesOrder(updateData);

      if (result.status) {
        await addLogDetails(
          { ...enquiry, SONumber: salesOrderData.SONumber, Action: "Update Sales Order" },
          "Update",
          `SK#Bid#${enquiry.EnquiryNumber}#${enquiry.CustomerEmail}`,
          "UpdateSalesOrderLog",
          { userId: context.userId, userName: context.userName, role: context.role },
          "Transaction"
        );

        MySwal.fire({
          text: "Sales Order updated successfully!",
          imageUrl: successImage,
          customClass: "swal-success",
        }).then(() => {
          onHide();
          setReload(!reload);
        });
      } else {
        MySwal.fire({
          text: result.message || "Failed to update Sales Order",
          imageUrl: errorImage,
          customClass: "swal-error",
        });
      }
    } catch (error) {
      MySwal.fire({
        text: "Failed to update Sales Order. Please try again.",
        imageUrl: errorImage,
        customClass: "swal-error",
      });
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static" centered>
      <Modal.Header closeButton>
        <Modal.Title>Update Sales Order</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col lg={4} xs={12}>
              <Form.Group className="g-input">
                <Form.Control
                  id="EnquiryNumber"
                  type="text"
                  name="EnquiryNumber"
                  placeholder=" "
                  value={enquiry.EnquiryNumber}
                  readOnly
                />
                <Form.Label htmlFor="EnquiryNumber">Enquiry Number</Form.Label>
              </Form.Group>
            </Col>
            <Col lg={4} xs={12}>
              <Form.Group className="g-input">
                <Form.Control
                  id="InsertedOn"
                  type="text"
                  name="InsertedOn"
                  placeholder=" "
                  value={enquiry.InsertedOn ? formatDateTimeInDDMMYYYY(enquiry.InsertedOn) : ""}
                  readOnly
                />
                <Form.Label htmlFor="InsertedOn">Enquiry Date</Form.Label>
              </Form.Group>
            </Col>
            <Col lg={4} xs={12}>
              <Form.Group className="g-input">
                <Form.Control
                  id="EnquirySource"
                  type="text"
                  name="EnquirySource"
                  placeholder=" "
                  value={formatEnquirySource(enquiry.EnquirySource)}
                  readOnly
                />
                <Form.Label htmlFor="EnquirySource">Contract/Non-Contract</Form.Label>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col lg={4} xs={12}>
              <Form.Group className="g-input">
                <Form.Control
                  id="ItemCode"
                  type="text"
                  name="ItemCode"
                  placeholder=" "
                  value={enquiry.ItemCode}
                  readOnly
                />
                <Form.Label htmlFor="ItemCode">Item Code</Form.Label>
              </Form.Group>
            </Col>
            <Col lg={4} xs={12}>
              <Form.Group className="g-input">
                <Form.Control
                  id="ItemDescription"
                  type="text"
                  name="ItemDescription"
                  placeholder=" "
                  value={enquiry.ItemDescription}
                  readOnly
                />
                <Form.Label htmlFor="ItemDescription">Item Description</Form.Label>
              </Form.Group>
            </Col>
            <Col lg={4} xs={12}>
              <Form.Group className="g-input">
                <Form.Control
                  id="ItemDetail"
                  as="textarea"
                  rows={1}
                  name="ItemDetail"
                  placeholder=" "
                  value={enquiry.ItemDetail}
                  readOnly
                />
                <Form.Label htmlFor="ItemDetail">Item Detail</Form.Label>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col lg={4} xs={12}>
              <Form.Group className="g-input">
                <Form.Control
                  id="ItemType"
                  type="text"
                  name="ItemType"
                  placeholder=" "
                  value={enquiry.ItemType}
                  readOnly
                />
                <Form.Label htmlFor="ItemType">Item Type</Form.Label>
              </Form.Group>
            </Col>
            <Col lg={4} xs={12}>
              <Form.Group className="g-input">
                <Form.Control
                  id="Quantity"
                  type="text"
                  name="Quantity"
                  placeholder=" "
                  value={enquiry.Quantity}
                  readOnly
                />
                <Form.Label htmlFor="Quantity">Quantity</Form.Label>
              </Form.Group>
            </Col>
            <Col lg={4} xs={12}>
              <Form.Group className="g-input">
                <Form.Control
                  id="UOM"
                  type="text"
                  name="UOM"
                  placeholder=" "
                  value={enquiry.UOM}
                  readOnly
                />
                <Form.Label htmlFor="UOM">UOM</Form.Label>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col lg={4} xs={12}>
              <Form.Group className="g-input">
                <Form.Control
                  id="GST"
                  type="text"
                  name="GST"
                  placeholder=" "
                  value={enquiry.GST}
                  readOnly
                />
                <Form.Label htmlFor="GST">GST%</Form.Label>
              </Form.Group>
            </Col>
            <Col lg={4} xs={12}>
              <Form.Group className="g-input">
                <Form.Control
                  id="ProductClass"
                  type="text"
                  name="ProductClass"
                  placeholder=" "
                  value={enquiry.ProductClass}
                  readOnly
                />
                <Form.Label htmlFor="ProductClass">Product Class</Form.Label>
              </Form.Group>
            </Col>
            <Col lg={4} xs={12}>
              <Form.Group className="g-input">
                <Form.Control
                  id="CommodityCode"
                  type="text"
                  name="CommodityCode"
                  placeholder=" "
                  value={enquiry.CommodityCode}
                  readOnly
                />
                <Form.Label htmlFor="CommodityCode">Commodity Code</Form.Label>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col lg={4} xs={12}>
              <Form.Group className="g-input">
                <Form.Control
                  id="Remarks"
                  as="textarea"
                  rows={1}
                  name="Remarks"
                  placeholder=" "
                  value={enquiry.Remarks || ""}
                  readOnly
                />
                <Form.Label htmlFor="Remarks">Remarks</Form.Label>
              </Form.Group>
            </Col>
            <Col lg={4} xs={12}>
              <Form.Group className="g-input">
                <Form.Control
                  id="Location"
                  type="text"
                  name="Location"
                  placeholder=" "
                  value={enquiry.Location}
                  readOnly
                />
                <Form.Label htmlFor="Location">Location</Form.Label>
              </Form.Group>
            </Col>
            <Col lg={4} xs={12}>
              <Form.Group className="g-input">
                <Form.Control
                  id="DueDate"
                  type="text"
                  name="DueDate"
                  placeholder=" "
                  value={enquiry.DueDate ? formatDateInDDMMYYYY(enquiry.DueDate) : ""}
                  readOnly
                />
                <Form.Label htmlFor="DueDate">Due Date</Form.Label>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col lg={4} xs={12}>
              <Form.Group className="g-input">
                <Form.Control
                  id="Division"
                  type="text"
                  name="Division"
                  placeholder=" "
                  value={enquiry.Division}
                  readOnly
                />
                <Form.Label htmlFor="Division">Division</Form.Label>
              </Form.Group>
            </Col>
            <Col lg={4} xs={12}>
              <Form.Group className="g-input">
                <Form.Control
                  id="LogisticCompany"
                  type="text"
                  name="LogisticCompany"
                  placeholder=" "
                  value={enquiry.LogisticCompany}
                  readOnly
                />
                <Form.Label htmlFor="LogisticCompany">Logistic Company</Form.Label>
              </Form.Group>
            </Col>
            {enquiry.EnquirySource === "Disposal" && (
              <Col lg={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="AttachDocument1"
                    type="text"
                    name="AttachDocument1"
                    placeholder=" "
                    value={enquiry.AttachDocument1}
                    readOnly
                    style={{ cursor: 'pointer', color: '#007bff' }}
                    onClick={() => handleDownload(enquiry.AttachDocument1File?.path, enquiry.AttachDocument1)}
                  />
                  <Form.Label htmlFor="AttachDocument1">Attachment 1</Form.Label>
                </Form.Group>
              </Col>
            )}
          </Row>

          {enquiry.EnquirySource === "Disposal" && (
            <Row>

              <Col lg={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="AttachDocument2"
                    type="text"
                    name="AttachDocument2"
                    placeholder=" "
                    value={enquiry.AttachDocument2}
                    readOnly
                    style={{ cursor: 'pointer', color: '#007bff' }}
                    onClick={() => handleDownload(enquiry.AttachDocument2File?.path, enquiry.AttachDocument2)}
                  />
                  <Form.Label htmlFor="AttachDocument2">Attachment 2</Form.Label>
                </Form.Group>
              </Col>


              <Col lg={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="AttachDocument3"
                    type="text"
                    name="AttachDocument3"
                    placeholder=" "
                    value={enquiry.AttachDocument3}
                    readOnly
                    style={{ cursor: 'pointer', color: '#007bff' }}
                    onClick={() => handleDownload(enquiry.AttachDocument3File?.path, enquiry.AttachDocument3)}
                  />
                  <Form.Label htmlFor="AttachDocument3">Attachment 3</Form.Label>
                </Form.Group>
              </Col>


              <Col lg={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="AttachImage"
                    type="text"
                    name="AttachImage"
                    placeholder=" "
                    value={enquiry.AttachImage}
                    readOnly
                    style={{ cursor: 'pointer', color: '#007bff' }}
                    onClick={() => handleDownload(enquiry.AttachImageFile?.path, enquiry.AttachImage)}
                  />
                  <Form.Label htmlFor="AttachImage">Image Attachment</Form.Label>
                </Form.Group>
              </Col>

            </Row>
          )}

          <Row>
            {(enquiry.EnquirySource === "Contract" || enquiry.EnquirySource === "Manual") && (
              <Col lg={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="ImagesAttachment"
                    type="text"
                    name="ImagesAttachment"
                    placeholder=" "
                    value={enquiry.ImagesAttachment}
                    readOnly
                    style={{ cursor: 'pointer', color: '#007bff' }}
                    onClick={() => handleDownload(enquiry.ImagesAttachmentFile?.path, enquiry.ImagesAttachment)}
                  />
                  <Form.Label htmlFor="ImagesAttachment">Attachment</Form.Label>
                </Form.Group>
              </Col>
            )}
            <Col lg={4} xs={12}>
              <Form.Group className="g-input">
                <Form.Control
                  id="CustomerUserName"
                  type="text"
                  name="CustomerUserName"
                  placeholder=" "
                  value={enquiry.CustomerUserName}
                  readOnly
                />
                <Form.Label htmlFor="CustomerUserName">Customer Name</Form.Label>
              </Form.Group>
            </Col>
            <Col lg={4} xs={12}>
              <Form.Group className="g-input">
                <Form.Control
                  id="CurrentBidDate"
                  type="text"
                  name="CurrentBidDate"
                  placeholder=" "
                  value={enquiry.CurrentBidDate}
                  readOnly
                />
                <Form.Label htmlFor="CurrentBidDate">Final Bid Date</Form.Label>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col md={12}>
              <h5>Finalized Customer</h5>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>BP Code</th>
                    <th>BP Name</th>
                    <th>Price(₹)</th>
                    <th>Sales Order Number</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{enquiry.BPCode || enquiry.CustomerCode}</td>
                    <td>{enquiry.BPName || enquiry.LinkedBPName}</td>
                    <td>₹{enquiry.CurrentBid}</td>
                    <td>
                      <Form.Control
                        type="text"
                        value={salesOrderData.SONumber}
                        onChange={(e) => handleSalesOrderChange(e.target.value)}
                        maxLength={550}
                        isInvalid={!!errors.salesOrder}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.salesOrder}
                      </Form.Control.Feedback>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button className="semiBoldToggleButton cancelBtn" variant="outline-success" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="success" className="btn-add-color-submit semiBoldToggleButton" onClick={handleSave}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UpdateSalesOrderModal;
